import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useCRM } from '../context/CRMContext';
import { format } from 'date-fns';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import WorkForm from '../components/Forms/WorkForm';
import SafeIcon from '../common/SafeIcon';
import FloatingActionButton from '../components/UI/FloatingActionButton';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit, FiTrash2, FiCalendar, FiClock, FiActivity } = FiIcons;

const Works = () => {
  const { works, contracts, clients, addWork, updateWork, deleteWork } = useCRM();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWork, setEditingWork] = useState(null);

  const handleAddWork = () => {
    setEditingWork(null);
    setIsModalOpen(true);
  };

  const handleEditWork = (work) => {
    setEditingWork(work);
    setIsModalOpen(true);
  };

  const handleSubmit = (data) => {
    if (editingWork) {
      updateWork(editingWork.id, data);
    } else {
      addWork(data);
    }
    setIsModalOpen(false);
    setEditingWork(null);
  };

  const handleDeleteWork = (id) => {
    if (window.confirm('Are you sure you want to delete this work item?')) {
      deleteWork(id);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'not-started': return 'bg-gray-100 text-gray-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'on-hold': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCompletionPercentage = (work) => {
    if (!work.hoursEstimated || work.hoursEstimated === 0) return 0;
    const spent = work.hoursSpent || 0;
    const estimated = work.hoursEstimated || 1;
    return Math.min(Math.round((spent / estimated) * 100), 100);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Works</h1>
        <Button onClick={handleAddWork}>
          <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
          Add Work
        </Button>
      </div>

      {works?.length === 0 ? (
        <Card className="p-12 text-center">
          <SafeIcon icon={FiActivity} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No works yet</h3>
          <p className="text-gray-500 mb-4">Track work items for your contracts here.</p>
          <Button onClick={handleAddWork}>
            <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
            Add Work
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {works?.map((work, index) => {
            const contract = contracts.find(c => c.id === work.contractId);
            const client = clients.find(c => c.id === contract?.clientId);
            const completionPercentage = getCompletionPercentage(work);

            return (
              <motion.div
                key={work.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {work.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {contract?.title} - {client?.name}
                      </p>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleEditWork(work)}
                        className="text-gray-400 hover:text-primary-600 transition-colors"
                      >
                        <SafeIcon icon={FiEdit} className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteWork(work.id)}
                        className="text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(work.status)}`}>
                      {work.status.replace('-', ' ')}
                    </span>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" />
                      <span>
                        Started: {format(new Date(work.startDate), 'MMM dd, yyyy')}
                        {work.endDate && ` - Due: ${format(new Date(work.endDate), 'MMM dd, yyyy')}`}
                      </span>
                    </div>

                    {(work.hoursSpent || work.hoursEstimated) && (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center text-gray-600">
                            <SafeIcon icon={FiClock} className="h-4 w-4 mr-2" />
                            <span>
                              {work.hoursSpent || 0} / {work.hoursEstimated || 0} hours
                            </span>
                          </div>
                          <span className="text-sm font-medium">
                            {completionPercentage}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-primary-600 h-2 rounded-full" 
                            style={{ width: `${completionPercentage}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {work.daysEstimated && (
                      <div className="flex items-center text-sm text-gray-600">
                        <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" />
                        <span>Estimated: {work.daysEstimated} days</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-1">Description</h4>
                    <p className="text-sm text-gray-600 line-clamp-3">{work.description}</p>
                  </div>

                  {work.notes && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-900 mb-1">Notes</h4>
                      <p className="text-sm text-gray-600">{work.notes}</p>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      Created {format(new Date(work.createdAt), 'MMM dd, yyyy')}
                    </p>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingWork ? 'Edit Work' : 'Add New Work'}
        size="xl"
      >
        <WorkForm
          work={editingWork}
          onSubmit={handleSubmit}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>

      <FloatingActionButton onClick={handleAddWork} />
    </div>
  );
};

export default Works;